import { memo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { At, DotsThree } from "@/components/ui/icons";
import { NavRow } from "@/components/ui/nav-row";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import type { DmActions } from "./use-dm-actions";

interface GroupDmRowProps {
  channel: { id: string; name: string };
  isSelected: boolean;
  actions: DmActions;
}

/**
 * Group-DM row in the sidebar. Differs from the 1:1 DM row in that it
 * doesn't have an avatar/presence — group DMs are name-only.
 *
 * Wrapped in `memo()` + consumes `actions: DmActions` (stable ref) so
 * identity-stable props let `GroupDmRow.memo` bail out cleanly on
 * Sidebar re-renders.
 */
export const GroupDmRow = memo(function GroupDmRow({
  channel,
  isSelected,
  actions,
}: GroupDmRowProps) {
  return (
    <div className="group/dm relative">
      <NavRow
        data-sidebar-channel-id={channel.id}
        isSelected={isSelected}
        onClick={() => actions.selectChannel(channel.id)}
        className="pr-8"
      >
        <At size={14} className="shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">{channel.name}</span>
      </NavRow>
      <div className="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 items-center group-hover/dm:flex group-focus-within/dm:flex has-[[data-state=open]]:flex">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <ToolbarIconButton
              className="data-[state=open]:bg-foreground/10"
              title="Conversation actions"
              aria-label={`Actions for ${channel.name}`}
            >
              <DotsThree size={14} className="text-muted-foreground" />
            </ToolbarIconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => actions.hideChannel(channel.id)}
              className="cursor-pointer"
            >
              Hide conversation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
});
